import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { DollarSign, Target, Brain, Sparkles, CheckCircle, Building, Lightbulb, ArrowRight, Loader2, FileText, Download, Lock } from 'lucide-react';
import ShareButton from '../ShareButton';
import { CareerAnalysisData, safeParseCareerAnalysis } from '../../types/analysis';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore';

interface CareerAnalysisProps {
  analysis: CareerAnalysisData | null;
}

export default function CareerAnalysis({ analysis }: CareerAnalysisProps) {
  const { plan } = useSubscriptionStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  
  const isPremium = plan === 'premium';

  // Safely parse career paths and other data
  const parsedData = React.useMemo(() => {
    if (!analysis) return null;
    
    // Obsługa parsowania zarówno obiektu jak i stringa JSON
    const parsed = typeof analysis === 'string' 
      ? safeParseCareerAnalysis(analysis)
      : analysis;
      
    return parsed;
  }, [analysis]);
  
  const careerPaths = parsedData?.sciezkiKariery || [];
  const predispositions = parsedData?.predyspozycje || [];

  const handleDownloadPdf = async () => {
    toast.error('Funkcja pobierania PDF jest tymczasowo niedostępna.');
  };

  if (!careerPaths.length || !predispositions.length) {
    return (
      <div className="bg-white p-6 rounded-2xl shadow-xl mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Analiza Kariery</h2>
        <p className="text-gray-600 mb-4">
          Brak analizy kariery. Wykonaj quiz aby wygenerować analizę.
        </p>
        <button
          onClick={() => navigate('/#quiz')}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
        >
          Rozpocznij Quiz
        </button>
      </div>
    );
  }

  return (
    <motion.div
      className="bg-white p-8 rounded-2xl shadow-xl relative overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Twoja Analiza Kariery</h2>
        <div className="flex gap-2">
          <ShareButton text="Sprawdź moją analizę kariery z KarierazAI.pl!" />
          <button
            onClick={handleDownloadPdf}
            disabled={true}
            className={`flex items-center gap-2 px-3 py-2 rounded-md transition-colors bg-gray-100 text-gray-400 cursor-not-allowed`}
            title={'Funkcja pobierania PDF tymczasowo niedostępna'}
          >
            <Download className="h-4 w-4" />
            <span>Pobierz PDF</span>
          </button>
        </div>
      </div>

      {/* Wprowadzenie */}
      <div className="mb-8">
        <p className="text-gray-700 text-lg">
          Na podstawie Twoich odpowiedzi w quizie, przeprowadziliśmy szczegółową analizę Twoich predyspozycji zawodowych. 
          Poniżej znajdziesz spersonalizowaną analizę kariery dostosowaną do Twojego profilu.
        </p>
      </div>

      {/* Rekomendowane Ścieżki Kariery */}
      <div className="mb-8 relative">
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
          <Target className="mr-2 h-6 w-6 text-purple-600" />
          Rekomendowane Ścieżki Kariery
        </h3>
        
        <p className="text-gray-600 mb-6">
          Na podstawie Twoich predyspozycji, te zawody będą najlepiej dopasowane do Twojego profilu:
        </p>
        
        <div className="space-y-6">
          {/* Pierwsza ścieżka zawsze widoczna */}
          {careerPaths.slice(0, 1).map((career, index) => (
            <motion.div
              key={index}
              className="border border-purple-200 rounded-lg p-5 hover:border-purple-500 transition-colors"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center">
                  <div className="bg-purple-100 p-2 rounded-full mr-3">
                    <Building className="h-5 w-5 text-purple-700" />
                  </div>
                  <h4 className="font-bold text-gray-800 text-lg">{career.nazwa}</h4>
                </div>
                <div className="flex items-center bg-green-50 px-3 py-1 rounded-full text-green-700 whitespace-nowrap min-w-max">
                  <DollarSign className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span className="text-sm font-medium">
                    {career.zarobki ? `${career.zarobki.min} - ${career.zarobki.max} ${career.zarobki.waluta}` : 'Brak danych'}
                  </span>
                </div>
              </div>

              <p className="text-gray-700 mb-4">{career.opis}</p>

              {career.wymagania && (
                <div className="mt-4">
                  <h5 className="font-semibold text-gray-700 mb-2 flex items-center">
                    <CheckCircle className="h-4 w-4 text-blue-500 mr-2" />
                    Wymagania:
                  </h5>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
                    {career.wymagania.map((req, idx) => (
                      <li key={idx} className="text-sm">{req}</li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          ))}
          
          {/* Reszta ścieżek z efektem blur dla wersji free */}
          <div className="relative">
            {careerPaths.slice(1).map((career, index) => (
              <motion.div
                key={index + 1}
                className={`border border-purple-200 rounded-lg p-5 mb-6 transition-colors select-none ${
                  !isPremium 
                    ? 'blur-[6px] pointer-events-none'
                    : 'hover:border-purple-500'
                }`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: (index + 1) * 0.1 }}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center">
                    <div className="bg-purple-100 p-2 rounded-full mr-3">
                      <Building className="h-5 w-5 text-purple-700" />
                    </div>
                    <h4 className="font-bold text-gray-800 text-lg">{career.nazwa}</h4>
                  </div>
                  <div className="flex items-center bg-green-50 px-3 py-1 rounded-full text-green-700 whitespace-nowrap min-w-max">
                    <DollarSign className="h-4 w-4 mr-1 flex-shrink-0" />
                    <span className="text-sm font-medium">
                      {career.zarobki ? `${career.zarobki.min} - ${career.zarobki.max} ${career.zarobki.waluta}` : 'Brak danych'}
                    </span>
                  </div>
                </div>

                <p className="text-gray-700 mb-4">{career.opis}</p>

                {career.wymagania && (
                  <div className="mt-4">
                    <h5 className="font-semibold text-gray-700 mb-2 flex items-center">
                      <CheckCircle className="h-4 w-4 text-blue-500 mr-2" />
                      Wymagania:
                    </h5>
                    <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
                      {career.wymagania.map((req, idx) => (
                        <li key={idx} className="text-sm">{req}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            ))}
            
            {/* Overlay z przyciskiem Premium dla wersji free */}
            {!isPremium && careerPaths.length > 1 && (
              <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-auto">
                <div className="bg-white/95 p-6 rounded-xl shadow-lg text-center">
                  <FileText className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                  <h3 className="font-bold text-gray-800 text-xl mb-2">Odblokuj pełną analizę kariery</h3>
                  <p className="text-gray-600 mb-4">
                    W planie Premium uzyskasz dostęp do wszystkich 4-5 rekomendowanych ścieżek kariery
                    oraz możliwość pobrania raportu PDF.
                  </p>
                  <button
                    onClick={() => navigate('/plany')}
                    className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    Przejdź do Premium
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Główne predyspozycje (skrócone) */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
          <Brain className="mr-2 h-6 w-6 text-purple-600" />
          Twoje Główne Predyspozycje
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {predispositions.slice(0, 2).map((pred, index) => (
            <div
              key={index}
              className="border border-purple-200 rounded-lg p-4 hover:border-purple-400 transition-colors"
            >
              <h4 className="font-semibold text-gray-800 text-lg mb-2">{pred.nazwa}</h4>
              <div 
                className={`${!isPremium ? 'relative select-none' : ''}`}
              >
                <p className={`text-gray-700 ${!isPremium ? 'line-clamp-2' : ''}`}>
                  {pred.uzasadnienie}
                </p>
                {!isPremium && (
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white pointer-events-none"></div>
                )}
              </div>
              {!isPremium && (
                <button 
                  onClick={() => navigate('/plany')}
                  className="mt-3 text-purple-600 text-sm font-medium flex items-center"
                >
                  <Lock className="h-3.5 w-3.5 mr-1" />
                  Odblokuj pełne uzasadnienie
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
